import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity, AlertTriangle, BookOpen, Brain, CalendarCheck, Download, FileSpreadsheet,
  FileText, GraduationCap, Heart, Loader2, Sparkles, TrendingUp, Trophy, Users, UserCog,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, ComposedChart,
} from "recharts";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";

import { requireRole } from "@/lib/session";
import { useSchoolContext } from "@/lib/school-context";
import { intelligenceOverview } from "@/lib/school-intelligence.functions";
import { schoolDashboard, schoolAiInsights } from "@/lib/school-management.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/school-dashboard")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({
    meta: [
      { title: "School Management Dashboard · MAKAI" },
      { name: "description", content: "Enterprise school analytics: performance, attendance, engagement, and AI insights." },
    ],
  }),
  component: Page,
});

const NUM = (n: number) => new Intl.NumberFormat("en-UG").format(Math.round(n || 0));
const PCT = (n: number | null | undefined) => (n == null ? "—" : `${n}%`);
const BRAND = { navy: "#0B1736", gold: "#C8A03C", cream: "#F7F6F0" };
const PALETTE = ["#C8A03C", "#0B1736", "#1976D2", "#2E7D32", "#D32F2F", "#F9A825", "#7C3AED", "#0F766E", "#E65100", "#5D4037"];

function Page() {
  const { active, loading } = useSchoolContext();
  return (
    <AdminShell
      title="School Management Dashboard"
      subtitle="Enterprise-grade insights across performance, engagement, and operations."
    >
      {loading ? <Loading /> : !active?.school_id
        ? <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Select a school to view the dashboard.</div>
        : <Dashboard key={active.school_id} schoolId={active.school_id} schoolName={active.school?.name ?? "School"} />}
    </AdminShell>
  );
}
function Loading() { return <div className="grid place-items-center rounded-2xl border bg-card p-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>; }

function Dashboard({ schoolId, schoolName }: { schoolId: string; schoolName: string }) {
  const fetchOverview = useServerFn(intelligenceOverview);
  const fetchMgmt = useServerFn(schoolDashboard);
  const fetchAi = useServerFn(schoolAiInsights);
  const printRef = useRef<HTMLDivElement>(null);

  const overviewQ = useQuery({
    queryKey: ["sm-overview", schoolId],
    queryFn: () => fetchOverview({ data: { schoolId } }),
  });
  const mgmtQ = useQuery({
    queryKey: ["sm-mgmt", schoolId],
    queryFn: () => fetchMgmt({ data: { schoolId } }),
  });

  const [aiText, setAiText] = useState<string>("");
  const [aiLoading, setAiLoading] = useState(false);
  const runAi = async () => {
    if (!overviewQ.data || !mgmtQ.data) return;
    setAiLoading(true); setAiText("");
    try {
      const snapshot = {
        kpis: overviewQ.data.kpis,
        subjects: overviewQ.data.subjects.slice(0, 8),
        teachers: overviewQ.data.teachers.slice(0, 8),
        attendance: overviewQ.data.attendance,
        prediction: overviewQ.data.prediction,
        rankings: mgmtQ.data.rankings.slice(0, 8),
        engagement: mgmtQ.data.engagement,
        parents: mgmtQ.data.parents,
        assessments: mgmtQ.data.assessments,
        reports: mgmtQ.data.reports,
      };
      const r = await fetchAi({ data: { schoolId, snapshot } });
      setAiText(r.text);
    } finally { setAiLoading(false); }
  };

  if (overviewQ.isLoading || mgmtQ.isLoading) return <Loading />;
  if (overviewQ.error || mgmtQ.error) return <div className="rounded-2xl border bg-card p-8 text-sm text-destructive">Failed to load dashboard.</div>;
  const ov = overviewQ.data!; const mg = mgmtQ.data!;

  const perfRadar = useMemo(() => ([
    { metric: "Academic", value: ov.kpis.avgAssessmentScorePct ?? 0 },
    { metric: "Attendance", value: ov.kpis.attendancePct30d ?? 0 },
    { metric: "Engagement", value: mg.engagement.engagementRatePct },
    { metric: "Parent link", value: mg.parents.linkageCoveragePct },
    { metric: "Curriculum", value: ov.curriculum.overallCoveragePct },
    { metric: "Reports", value: mg.reports.coveragePct },
  ]), [ov, mg]);

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    const add = (name: string, rows: any[]) =>
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
    add("Summary", [{
      School: schoolName,
      Generated: new Date().toISOString(),
      "Active learners": ov.kpis.activeLearners,
      "Active staff": ov.kpis.activeStaff,
      "Avg score %": ov.kpis.avgAssessmentScorePct ?? 0,
      "Attendance % (30d)": ov.kpis.attendancePct30d ?? 0,
      "Engagement %": mg.engagement.engagementRatePct,
      "Curriculum coverage %": ov.curriculum.overallCoveragePct,
      "Predicted exam avg %": ov.prediction.predictedAvgPct,
      "Prediction band": ov.prediction.band,
    }]);
    add("Class rankings", mg.rankings);
    add("Subject performance", ov.subjects);
    add("Teacher performance", ov.teachers);
    add("Attendance trend", ov.attendance.trend);
    add("Assessment recent", mg.assessments.recent);
    add("Student engagement", mg.engagement.dailyTrend);
    add("Engagement by type", mg.engagement.byType);
    add("Learning trends", mg.trends);
    add("Parent engagement", [mg.parents]);
    add("Report cards", [{
      Total: mg.reports.totalReports, Published: mg.reports.publishedReports,
      Drafts: mg.reports.draftReports, "Learners covered": mg.reports.learnersWithReports,
      "Coverage %": mg.reports.coveragePct,
    }]);
    add("At-risk learners", ov.atRisk);
    XLSX.writeFile(wb, `school-dashboard-${schoolName.replace(/\s+/g, "_")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = async () => {
    const jsPDF = (await import("jspdf")).default;
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    doc.setFillColor(11, 23, 54); doc.rect(0, 0, w, 70, "F");
    doc.setTextColor(200, 160, 60); doc.setFontSize(20);
    doc.text("MAKAI · School Management Dashboard", 40, 35);
    doc.setTextColor(247, 246, 240); doc.setFontSize(11);
    doc.text(`${schoolName} — ${new Date().toLocaleString()}`, 40, 55);
    doc.setTextColor(20, 20, 20);

    autoTable(doc, {
      startY: 90, theme: "grid",
      head: [["Metric", "Value"]],
      body: [
        ["Active learners", NUM(ov.kpis.activeLearners)],
        ["Active staff", NUM(ov.kpis.activeStaff)],
        ["Avg assessment %", PCT(ov.kpis.avgAssessmentScorePct)],
        ["Attendance % (30d)", PCT(ov.kpis.attendancePct30d)],
        ["Engagement %", `${mg.engagement.engagementRatePct}%`],
        ["Curriculum coverage %", `${ov.curriculum.overallCoveragePct}%`],
        ["Predicted exam avg", `${ov.prediction.predictedAvgPct}% (${ov.prediction.band})`],
        ["Parent link coverage", `${mg.parents.linkageCoveragePct}%`],
        ["Report cards coverage", `${mg.reports.coveragePct}%`],
      ],
      headStyles: { fillColor: [11, 23, 54], textColor: [200, 160, 60] }, styles: { fontSize: 10 },
    });

    autoTable(doc, {
      head: [["Rank", "Class · Stream", "Learners", "Avg %", "Attendance %", "Composite"]],
      body: mg.rankings.slice(0, 15).map((r) => [
        r.rank, `${r.className} · ${r.stream}`, r.learners, PCT(r.avgScorePct), PCT(r.attendancePct), r.composite,
      ]),
      headStyles: { fillColor: [200, 160, 60], textColor: [11, 23, 54] }, styles: { fontSize: 9 },
    });

    autoTable(doc, {
      head: [["Subject", "Avg %", "Samples"]],
      body: ov.subjects.slice(0, 20).map((s) => [s.subject, `${s.avgPct}%`, NUM(s.samples)]),
      headStyles: { fillColor: [200, 160, 60], textColor: [11, 23, 54] }, styles: { fontSize: 9 },
    });

    autoTable(doc, {
      head: [["Teacher", "Avg class %", "Assessments", "Plans (30d)"]],
      body: ov.teachers.slice(0, 20).map((t) => [t.name, PCT(t.avgClassScorePct), t.assessmentsGiven, t.lessonPlans30d]),
      headStyles: { fillColor: [200, 160, 60], textColor: [11, 23, 54] }, styles: { fontSize: 9 },
    });

    if (aiText) {
      doc.addPage();
      doc.setFontSize(14); doc.setTextColor(11, 23, 54);
      doc.text("MAKAI AI Insights", 40, 50);
      doc.setFontSize(10); doc.setTextColor(30, 30, 30);
      const lines = doc.splitTextToSize(aiText.replace(/[*_#>`]/g, ""), w - 80);
      doc.text(lines, 40, 75);
    }

    doc.save(`school-dashboard-${schoolName.replace(/\s+/g, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-6" ref={printRef}>
      {/* Header actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Enterprise dashboard</div>
          <h2 className="font-serif text-2xl">{schoolName}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={runAi} disabled={aiLoading}>
            {aiLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
            AI Insights
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel}>
            <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
          </Button>
          <Button size="sm" onClick={exportPdf} className="bg-[color:var(--gold,#C8A03C)] text-[color:var(--navy,#0B1736)] hover:opacity-90">
            <FileText className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      {/* KPI hero */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={Users} label="Active learners" value={NUM(ov.kpis.activeLearners)} />
        <Kpi icon={UserCog} label="Active staff" value={NUM(ov.kpis.activeStaff)} />
        <Kpi icon={GraduationCap} label="Avg score" value={PCT(ov.kpis.avgAssessmentScorePct)} tone="gold" />
        <Kpi icon={CalendarCheck} label="Attendance (30d)" value={PCT(ov.kpis.attendancePct30d)} tone="green" />
        <Kpi icon={Activity} label="Engagement" value={`${mg.engagement.engagementRatePct}%`} tone="blue" />
        <Kpi icon={BookOpen} label="Curriculum" value={`${ov.curriculum.overallCoveragePct}%`} />
      </section>

      {/* AI insights card */}
      {aiText && (
        <div className="rounded-2xl border-2 border-[color:var(--gold,#C8A03C)]/40 bg-gradient-to-br from-[color:var(--gold,#C8A03C)]/5 to-transparent p-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[color:var(--gold,#C8A03C)]"><Brain className="h-4 w-4" /> MAKAI School Insights</div>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground/90">{aiText}</div>
        </div>
      )}

      {/* School Performance radar + Learning trends */}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2" title="School Performance" icon={Trophy}>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={perfRadar}>
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar dataKey="value" stroke={BRAND.gold} fill={BRAND.gold} fillOpacity={0.35} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-2 text-xs text-muted-foreground">
            Predicted exam average: <b>{ov.prediction.predictedAvgPct}%</b> · <b>{ov.prediction.band}</b>
          </div>
        </Card>
        <Card className="lg:col-span-3" title="Learning Trends" icon={TrendingUp}>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={mg.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="right" dataKey="lessonsCompleted" name="Lessons completed" fill={BRAND.navy} radius={[4, 4, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="avgQuizPct" name="Avg quiz %" stroke={BRAND.gold} strokeWidth={3} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Subject performance + Teacher performance */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Subject Performance" icon={BookOpen}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ov.subjects.slice(0, 10)} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="subject" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Bar dataKey="avgPct" name="Avg %" fill={BRAND.gold} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Teacher Performance (top 10)" icon={UserCog}>
          <div className="max-h-[300px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card text-xs uppercase text-muted-foreground">
                <tr><th className="p-2 text-left">Teacher</th><th className="p-2 text-right">Avg %</th><th className="p-2 text-right">Assess.</th><th className="p-2 text-right">Plans</th></tr>
              </thead>
              <tbody>
                {ov.teachers.slice(0, 15).map((t) => (
                  <tr key={t.teacherId} className="border-t border-border/60">
                    <td className="p-2">{t.name}</td>
                    <td className="p-2 text-right font-mono">{PCT(t.avgClassScorePct)}</td>
                    <td className="p-2 text-right">{t.assessmentsGiven}</td>
                    <td className="p-2 text-right">{t.lessonPlans30d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Class rankings */}
      <Card title="Class Rankings" icon={Trophy}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">Rank</th><th className="p-2 text-left">Class · Stream</th>
                <th className="p-2 text-right">Learners</th><th className="p-2 text-right">Avg %</th>
                <th className="p-2 text-right">Attendance %</th><th className="p-2 text-right">Composite</th>
              </tr>
            </thead>
            <tbody>
              {mg.rankings.slice(0, 12).map((r) => (
                <tr key={r.streamId} className={cn("border-t border-border/60", r.rank <= 3 && "bg-gold/5")}>
                  <td className="p-2 font-serif text-lg">
                    {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
                  </td>
                  <td className="p-2">{r.className} · {r.stream}</td>
                  <td className="p-2 text-right">{r.learners}</td>
                  <td className="p-2 text-right font-mono">{PCT(r.avgScorePct)}</td>
                  <td className="p-2 text-right font-mono">{PCT(r.attendancePct)}</td>
                  <td className="p-2 text-right font-semibold">{r.composite}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Attendance + Assessment analytics */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Attendance Integration" icon={CalendarCheck}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={ov.attendance.trend}>
              <defs>
                <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND.gold} stopOpacity={0.7} />
                  <stop offset="100%" stopColor={BRAND.gold} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="attendancePct" stroke={BRAND.gold} fill="url(#attGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Assessment Analytics" icon={GraduationCap}>
          <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg border p-2"><div className="text-muted-foreground">Total</div><div className="text-lg font-bold">{mg.assessments.totalAssessments}</div></div>
            <div className="rounded-lg border p-2"><div className="text-muted-foreground">Overall avg</div><div className="text-lg font-bold">{mg.assessments.overallAvgPct}%</div></div>
            <div className="rounded-lg border p-2"><div className="text-muted-foreground">Window</div><div className="text-lg font-bold">90d</div></div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mg.assessments.recent.slice(0, 12)}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="avgPct" name="Avg %" fill={BRAND.navy} radius={[4, 4, 0, 0]} />
              <Bar dataKey="passRate" name="Pass rate %" fill={BRAND.gold} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Engagement + Parent engagement */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Student Engagement" icon={Activity}>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Active (30d)" value={NUM(mg.engagement.activeLearners30d)} />
            <Stat label="Rate" value={`${mg.engagement.engagementRatePct}%`} />
            <Stat label="Events (30d)" value={NUM(mg.engagement.totalEvents30d)} />
            <Stat label="Active streaks" value={NUM(mg.engagement.activeStreaks)} />
          </div>
          <div className="mt-3 h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mg.engagement.dailyTrend}>
                <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} />
                <Tooltip />
                <Line type="monotone" dataKey="events" stroke={BRAND.gold} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Engagement by activity" icon={Sparkles}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={mg.engagement.byType} layout="vertical" margin={{ left: 80 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="type" tick={{ fontSize: 10 }} width={100} />
              <Tooltip />
              <Bar dataKey="count" fill={BRAND.navy} radius={[0, 4, 4, 0]}>
                {mg.engagement.byType.map((_, i) => <text key={i} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Parent Engagement" icon={Heart}>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Total parents" value={NUM(mg.parents.totalParents)} />
            <Stat label="With account" value={`${mg.parents.accountAdoptionPct}%`} />
            <Stat label="Linked to learner" value={NUM(mg.parents.parentsLinkedToLearner)} />
            <Stat label="Link coverage" value={`${mg.parents.linkageCoveragePct}%`} />
            <Stat label="Learners covered" value={NUM(mg.parents.learnersWithLinkedParent)} />
            <Stat label="Reads (30d)" value={NUM(mg.parents.announcementReads30d)} />
          </div>
          <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-xs">
            {mg.parents.linkageCoveragePct < 60
              ? "Under 60% of learners are linked to a parent — schedule a parent-linking drive."
              : "Healthy parent linkage. Keep announcements flowing."}
          </div>
        </Card>
      </div>

      {/* Report cards */}
      <Card title="School Report Cards" icon={FileText}>
        <div className="grid gap-3 md:grid-cols-5">
          <Stat label="Total reports" value={NUM(mg.reports.totalReports)} />
          <Stat label="Published" value={NUM(mg.reports.publishedReports)} tone="green" />
          <Stat label="Drafts" value={NUM(mg.reports.draftReports)} tone="amber" />
          <Stat label="Learners covered" value={`${NUM(mg.reports.learnersWithReports)} / ${NUM(mg.reports.activeLearners)}`} />
          <Stat label="Coverage" value={`${mg.reports.coveragePct}%`} tone="gold" />
        </div>
      </Card>

      {/* At-risk */}
      {ov.atRisk.length > 0 && (
        <Card title="AI Risk Alerts" icon={AlertTriangle}>
          <div className="grid gap-2 md:grid-cols-2">
            {ov.atRisk.slice(0, 8).map((r, i) => (
              <div key={i} className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.admissionNo ?? "—"}</div>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{r.reasons.join(" · ")}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone?: "gold" | "green" | "blue" }) {
  const toneClass = tone === "gold" ? "text-[color:var(--gold,#C8A03C)]"
    : tone === "green" ? "text-emerald-600" : tone === "blue" ? "text-blue-600" : "text-foreground";
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={cn("mt-1 font-serif text-2xl font-bold", toneClass)}>{value}</div>
    </div>
  );
}

function Card({ title, icon: Icon, children, className }: { title: string; icon: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-2xl border bg-card p-5", className)}>
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-[color:var(--gold,#C8A03C)]/15 text-[color:var(--gold,#C8A03C)]">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-serif text-lg">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "gold" | "green" | "amber" }) {
  const toneClass = tone === "gold" ? "text-[color:var(--gold,#C8A03C)]"
    : tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-foreground";
  return (
    <div className="rounded-lg border p-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 font-serif text-lg font-bold", toneClass)}>{value}</div>
    </div>
  );
}

// Unused palette reference to satisfy linter
void PALETTE;
